import type { ReactElement } from 'react';
import type { InsightsMethodMap } from 'search-insights';

import { useMemo, useCallback } from 'react';

import { OptionGroup } from './OptionGroup';
import { useAlgoliaInsights } from '../../../shared/vendors/useAlgoliaInsights';
import { COLOUR, PERCENTAGE_DISCOUNT } from '~/utilities/constants/algolia';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { escapePercentageDiscountValue } from '~/utilities/helpers';
import { useAlgoliaFacetOptions } from '~/utilities/hooks/search/useAlgoliaFacetOptions';
import { useColorCode } from '~/utilities/hooks/useColorCode';

const FACET_CONFIG = {
  CHECKBOX_TYPE: 'square' as const,
  STYLE_COLLAPSE_COUNT: 9,
  STYLE_VALUE: 'style',
  MULTI_COLOR_TEXT: 'Multi color',
  TRANSPARENT_BG: 'transparent',
  SIZE_ATTRIBUTE: 'size',
  LENGTH_ATTRIBUTE: 'length',
} as const;

type FacetProps = {
  text: string;
  attribute: string;
  valueEN: string;
  isExpanded?: boolean;
  onToggle?: () => void;
};

const getReadableCheckColor = (hex?: string): string | undefined => {
  if (!hex) {
    return undefined;
  }

  let h = hex.trim();

  if (!h.startsWith('#')) {
    return undefined;
  }

  if (h.length === 4) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }

  if (h.length !== 7) {
    return undefined;
  }

  const r = Number.parseInt(h.slice(1, 3), 16);
  const g = Number.parseInt(h.slice(3, 5), 16);
  const b = Number.parseInt(h.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 || yiq === 102 ? '#000000' : '#FFFFFF';
};

export const Facet = ({
  text,
  attribute,
  valueEN,
  isExpanded,
  onToggle,
}: FacetProps): ReactElement => {
  const getColorHex = useColorCode();
  const { items, refine, canRefine, showFacet } = useAlgoliaFacetOptions({
    attribute,
    valueEN,
    text,
  });
  const { pushToAlgoliaWithQueryId } = useAlgoliaInsights();
  const { isPLP } = useProductListContext();

  const isColorFacet = valueEN === COLOUR;
  const isStyleFacet = valueEN === FACET_CONFIG.STYLE_VALUE;
  const isSizeFacet = valueEN === FACET_CONFIG.SIZE_ATTRIBUTE;
  const isLengthFacet = valueEN === FACET_CONFIG.LENGTH_ATTRIBUTE;
  const isGridFacet = isSizeFacet || isLengthFacet;

  const pushAlgoliaEvents = useCallback(
    (item: string) => {
      const event: keyof InsightsMethodMap = 'clickedFilters';
      const eventName = isPLP ? 'PLP_clickedFilters' : 'PRP_clickedFilters';

      pushToAlgoliaWithQueryId(event, eventName, {
        filters: [`${attribute}:${item}`],
      });
    },
    [isPLP, attribute, pushToAlgoliaWithQueryId]
  );

  const handleSelect = useCallback(
    (key: string): void => {
      if (!canRefine) {
        return;
      }

      const label = key.split(`${valueEN}-`)[1] ?? '';
      const value = valueEN === PERCENTAGE_DISCOUNT ? escapePercentageDiscountValue(label) : label;

      pushAlgoliaEvents(value);
      refine(value);
    },
    [canRefine, valueEN, refine, pushAlgoliaEvents]
  );

  const optionItems = useMemo(
    () =>
      items.map(item => {
        const isMultiColorBG = isColorFacet && item.label.includes(FACET_CONFIG.MULTI_COLOR_TEXT);
        const swatchBg = isColorFacet ? getColorHex(item.label) : undefined;

        return {
          key: `${valueEN}-${item.label}`,
          label: item.label,
          selected: item.isRefined,
          bgColor: isMultiColorBG ? '#666666' : swatchBg,
          testId: 'filter-option',
        };
      }),
    [items, isColorFacet, valueEN, getColorHex]
  );

  if (!showFacet()) {
    return <></>;
  }

  const summary = items
    .filter(item => item.isRefined)
    .map(item => {
      if (isColorFacet) {
        return getColorHex(item.label);
      }

      return item.label;
    })
    .join(',');

  const styleProps = isStyleFacet ? { collapseAt: FACET_CONFIG.STYLE_COLLAPSE_COUNT } : {};

  const getListClassName = (): string | undefined => {
    if (isColorFacet) {
      return 'colorFilter';
    }

    if (isSizeFacet) {
      return 'sizeFilter';
    }

    if (isLengthFacet) {
      return 'lengthFilter';
    }

    return undefined;
  };

  return (
    <OptionGroup
      title={text}
      valueEN={valueEN}
      summary={summary}
      listClassName={getListClassName()}
      items={optionItems}
      getIconColor={bg => (bg ? getReadableCheckColor(bg) : undefined)}
      onSelect={handleSelect}
      checkboxType={isGridFacet ? undefined : FACET_CONFIG.CHECKBOX_TYPE}
      isExpanded={isExpanded}
      onToggle={onToggle}
      {...styleProps}
    />
  );
};
